import { Button } from '@/components/ui/button'
import { SheetSectionCard } from '@/components/SheetSectionCard'
import { useCapability } from '@/features/permissions/hooks'
import { IdentifierRow } from '@/features/patients/components/IdentifierRow'
import { mergeSourceIds } from '@/features/patients/utils/sourceIds'
import { usePatientQuery } from '@/hooks/usePatientsQuery'

/** Resolve the client's current identifiers, independently of appointment provenance. */
export function AppointmentsClientIdentifiers({
  patientId,
}: {
  patientId: string | null
}) {
  const allowed = useCapability('data.clients.view') === true
  const query = usePatientQuery(patientId, allowed)
  if (!allowed || !patientId) return null
  const entries = mergeSourceIds(query.data?.source_ids)

  return (
    <SheetSectionCard
      title="Client identifiers"
      body={
        query.isPending ? (
          <p className="text-muted-foreground text-sm">
            Loading client identifiers…
          </p>
        ) : query.isError ? (
          <div className="space-y-2">
            <p className="text-muted-foreground text-sm">
              Unable to load client identifiers.
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void query.refetch()}>
              Retry
            </Button>
          </div>
        ) : query.data ? (
          <>
            <dl className="divide-y rounded-md border px-3">
              <IdentifierRow
                label="Careforce client ID"
                value={query.data.id}
              />
              {entries.map((entry) => (
                <IdentifierRow
                  key={`${entry.system}:${entry.value}`}
                  label={entry.label}
                  value={entry.value}
                />
              ))}
            </dl>
            {entries.length === 0 && (
              <p className="text-muted-foreground mt-2 text-sm">
                No source identifiers on file
              </p>
            )}
          </>
        ) : (
          <p className="text-muted-foreground text-sm">
            Linked client is unavailable.
          </p>
        )
      }
    />
  )
}
